import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CropField } from '@next/arable-lib/crop-field';
import { Store } from '@ngrx/store';
import { combineLatest, filter, Subject } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { TranslatePipe } from '@next/core-lib/i18n';
// eslint-disable-next-line max-len
import { SpecificFormHeaderSectionComponent } from '../../components/questionnaire/form-header-section/specific-form-header-section/specific-form-header-section.component';
import { RiskAnalysisComponent } from '../../components/risk-analysis/risk-analysis.component';
import { TgaMapCardComponent } from '../../components/tga-map-card/tga-map-card.component';
import { DisplayAdvice, TgaCropFieldWithStatus, TgaState, TranslationParams } from '../../shared/models/models';
import { TgaSharedService } from '../../shared/services/tga-shared.service';
import {
  fetchSoilAdvice,
  loadCropFields,
  loadTgaBiomassImage,
  loadTgaBiomassPotatoImage,
  loadTgaHeightImage,
  loadTgaSoilImage,
} from '../../store/tga.actions';
import {
  getTgaCropYear,
  selectCropFieldById,
  selectCropYearAndCropIdForSoilAdvice,
  selectSoilAdvice,
  selectSoilAdviceTranslationParams,
  TgaBiomassImageSelectors,
  TgaBiomassPotatoImageSelectors,
  TgaHeightImageSelectors,
  TgaSoilImageSelectors,
} from '../../store/tga.selectors';

@Component({
  selector: 'app-tga-soil-advice',
  templateUrl: './tga-soil-advice.component.html',
  styleUrls: ['./tga-soil-advice.component.scss'],
  imports: [SpecificFormHeaderSectionComponent, TgaMapCardComponent, RiskAnalysisComponent, TranslatePipe],
})
export class TGASoilAdviceComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject<Store<TgaState>>(Store);
  private readonly tgaSharedService = inject(TgaSharedService);

  readonly destroy$ = new Subject<void>();
  cropFieldId?: number;
  cropYear: number = new Date().getFullYear();
  cropField: CropField | null = null;
  isLoading = false;
  soilAdvice = signal<DisplayAdvice[] | null>(null);
  translationParams: TranslationParams = {};

  readonly elevationResource$ = this.store.select(TgaHeightImageSelectors.select);
  readonly soilResource$ = this.store.select(TgaSoilImageSelectors.select);
  readonly biomassResource$ = this.store.select(TgaBiomassImageSelectors.select);
  readonly biomassPotatoResource$ = this.store.select(TgaBiomassPotatoImageSelectors.select);
  private readonly params$ = this.route.params.pipe(takeUntil(this.destroy$));
  private readonly soilAdvice$ = this.store.select(selectSoilAdvice);
  private readonly cropYearAndCropId$ = this.store.select(selectCropYearAndCropIdForSoilAdvice);
  private readonly translationParams$ = this.store.select(selectSoilAdviceTranslationParams);

  ngOnInit(): void {
    this.store
      .select(getTgaCropYear)
      .pipe(take(1))
      .subscribe((year) => {
        this.cropYear = year;
        this.initializeParams();
        this.createCropFieldStream().pipe(takeUntil(this.destroy$)).subscribe();
        this.translationParams$.pipe(takeUntil(this.destroy$)).subscribe((params) => {
          this.translationParams = params;
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getEndDateYear(cropField?: CropField): number | null {
    if (!cropField) return this.cropYear;
    return this.tgaSharedService.getEndDateYear(cropField, this.cropYear);
  }

  private createCropFieldStream() {
    return this.params$.pipe(
      switchMap((params) => this.store.select(selectCropFieldById(params.cropFieldId))),
      tap(this.handleCropFieldResult.bind(this)),
    );
  }

  private handleCropFieldResult(cropFieldWithStatus: TgaCropFieldWithStatus | null) {
    if (!cropFieldWithStatus) {
      this.store.dispatch(loadCropFields({ cropYear: this.cropYear }));
    }
    this.cropField = cropFieldWithStatus?.cropField || null;
  }

  private initializeParams(): void {
    this.params$
      .pipe(
        filter((params) => !!params.cropFieldId),
        tap((params: { cropFieldId?: string | number; cropYear?: string | number }) => {
          if (params.cropFieldId != null) this.cropFieldId = Number(params.cropFieldId);
        }),
        tap((params) => {
          const imageActions = [loadTgaHeightImage, loadTgaSoilImage, loadTgaBiomassImage, loadTgaBiomassPotatoImage];
          for (const action of imageActions) {
            this.store.dispatch(action({ cropFieldId: Number(params.cropFieldId) }));
          }
        }),
        switchMap((params) => this.store.select(selectCropFieldById(Number(params.cropFieldId)))),
        tap((cropField) => this.handleCropFieldResult(cropField)),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.handleSoilAdviceLoading();
  }

  private handleSoilAdviceLoading(): void {
    combineLatest([this.params$, this.soilAdvice$, this.cropYearAndCropId$])
      .pipe(
        filter(([params]) => !!params.cropFieldId),
        tap(([params, soilAdvice, stateInfo]) => {
          const currentCropFieldId = Number(params.cropFieldId);
          const currentCropYear = this.cropYear;

          const needsToFetchAdvice =
            !soilAdvice || stateInfo.cropFieldId !== currentCropFieldId || stateInfo.cropYear !== currentCropYear;

          if (needsToFetchAdvice && currentCropFieldId) {
            this.isLoading = true;
            this.store.dispatch(
              fetchSoilAdvice({
                cropFieldId: currentCropFieldId,
                cropYear: currentCropYear,
              }),
            );
          } else if (soilAdvice) {
            this.soilAdvice.set(soilAdvice.data ?? null);
            this.isLoading = false;
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
